import { startsWithNumberWord } from "./item-input.js";

/**
 * Zerlegt eine Eingabe in mehrere Artikel.
 *
 * Gebraucht an zwei Stellen: in der Schnelleingabe, in die man auch diktieren
 * kann, und im lokalen Weg des Sprachassistenten. Am 20.09.2026 hat jemand
 * "Äpfel, Bananen, Milch und Klopapier" ins Feld diktiert und bekam einen
 * einzigen Artikel "Äpfel" mit der Notiz "Bananen, Milch und Klopapier".
 *
 * Der Knackpunkt ist das Komma. Es trennt Artikel ("Äpfel, Bananen"), aber es
 * leitet auch eine Notiz ein ("Mehl, gesiebt"). Im Deutschen entscheidet die
 * Großschreibung: Ein Artikel ist ein Hauptwort und beginnt groß, eine Notiz
 * ist meist eine Beschreibung und beginnt klein. Danach wird getrennt, und
 * "und" trennt immer.
 */

/** Höflichkeiten am Anfang, die kein Artikel sind. */
const EINLEITUNGEN = [
  "bitte",
  "ich brauche noch",
  "ich brauche",
  "ich bräuchte",
  "wir brauchen noch",
  "wir brauchen",
  "ich hätte gern",
  "kauf noch",
  "kaufe noch",
  "besorg noch",
  "besorge noch",
  "hol noch",
  "hole noch",
  "noch",
];

/** Diese Wörter trennen immer, unabhängig von der Schreibung. */
const STARKE_TRENNER = /\s*(?:;|\n|\bund\b|\bsowie\b|\bplus\b)\s*/giu;

/**
 * Ein Satzpunkt mitten in der Eingabe trennt ebenfalls. Die Transkription
 * macht aus einer langsam gesprochenen Aufzählung gern "Mandarinen. Äpfel.
 * Milch." Das Wort davor braucht vier Buchstaben, damit Abkürzungen wie
 * "Dr. Oetker" oder "Pkg. Milch" zusammenbleiben.
 */
const SATZPUNKT = /(?<=\p{L}{4})\.\s+(?=\p{L})/gu;

/**
 * Füllwörter, mit denen man beim Sprechen den nächsten Artikel ansagt:
 * "Mandarinen, außerdem noch Gurken". Sie fallen weg, und was folgt, ist ein
 * neuer Artikel, auch wenn er klein beginnt.
 */
const FUELLWOERTER = /^(?:(?:außerdem|ausserdem|dann|noch|auch|und)\b[\s,]*)+/iu;

function ohneEinleitung(text: string): string {
  let rest = text;
  for (const einleitung of EINLEITUNGEN) {
    const passt = new RegExp(`^${einleitung}\\b[\\s,:]*`, "iu");
    if (passt.test(rest)) {
      rest = rest.replace(passt, "").trim();
      break;
    }
  }
  return rest;
}

/**
 * Trennt an Kommas, aber nur vor etwas, das wie ein neuer Artikel aussieht:
 * Großbuchstabe, Ziffer, eine gesprochene Menge ("eine Gurke") oder ein
 * Füllwort davor ("außerdem Gurken"). Alles andere bleibt bei seinem Artikel
 * und wird dort zur Notiz.
 *
 * Am 16.10.2026 kam vom Mikrofon "Mandarinen, eine Gurke, zwei Tomaten, ein
 * Liter Milch": ein Artikel "Mandarinen", der Rest war seine Notiz.
 */
function kommaTeile(text: string): string[] {
  const stuecke = text.split(/\s*,\s*/u);
  const raus: string[] = [];

  for (const roh of stuecke) {
    const stueck = roh.replace(FUELLWOERTER, "").trim();
    const neuerArtikel = stueck !== roh || /^[\p{Lu}\d]/u.test(stueck) || startsWithNumberWord(stueck);
    const vorheriges = raus[raus.length - 1];
    if (vorheriges === undefined || neuerArtikel) raus.push(stueck);
    else raus[raus.length - 1] = `${vorheriges}, ${stueck}`;
  }

  return raus;
}

/**
 * Gibt die einzelnen Artikel zurück, in der gesprochenen Reihenfolge. Bei
 * einer gewöhnlichen Eingabe ist das genau ein Eintrag, und der Aufrufer
 * merkt von dieser Funktion nichts.
 */
export function splitItemInput(raw: string): string[] {
  // Zeilenumbrüche zuerst zu einem Trenner machen: Das Zusammenziehen der
  // Leerzeichen danach würde sie sonst verschlucken, und eine eingefügte
  // Liste wäre ein einziger Artikel.
  const sauber = ohneEinleitung(
    raw.replace(/[\r\n]+/gu, " ; ").replace(SATZPUNKT, " ; ").trim().replace(/\s+/gu, " ").replace(/[.!]+$/u, ""),
  );
  if (sauber.length === 0) return [];

  const teile = sauber
    .split(STARKE_TRENNER)
    .flatMap((stueck) => kommaTeile(stueck))
    .map((stueck) => stueck.trim())
    .filter((stueck) => stueck.length > 0);

  // Bleibt nach dem Zerlegen etwas übrig, das kein Wort enthält, war die
  // Trennung falsch. Dann lieber alles als einen Artikel lassen, als aus
  // einer Eingabe Bruchstücke zu machen.
  if (teile.length === 0 || teile.some((stueck) => !/\p{L}{2}/u.test(stueck))) return [sauber];

  return teile;
}
